import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  catchError,
  delay,
  filter,
  map,
  of,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { generateUUID } from '@shared/utils/common';
import {
  TuiDataListDropdownManagerModule,
  TuiInputModule,
  TuiInputNumberModule,
  TuiIslandModule,
} from '@taiga-ui/kit';
import {
  TuiButtonModule,
  TuiDataListModule,
  TuiDialogService,
  TuiDropdownModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/core';
import { TuiActiveZoneModule } from '@taiga-ui/cdk';
import { AlertService } from '@shared/services/alert.service';
import { CURRENCIES } from '@shared/constants/currency';
import { AutocompleteComponent } from '@shared/components/forms/autocomplete/autocomplete.component';
import {
  AttachmentsManagerComponent,
  AttachmentsManagerInputData,
} from '@shared/components/attachments-manager/attachments-manager.component';
import { PolymorpheusComponent } from '@tinkoff/ng-polymorpheus';
import { CargoCatalogsService } from '@shared/services/cargo-catalogs.service';
import { AsyncPipe } from '@angular/common';
import { TransportItemFormComponent } from '@shared/components/forms/transport-item-form/transport-item-form.component';
import {
  CarrierFormComponent,
  CarrierFormInputData,
} from '@shared/components/catalogs/carrier-form/carrier-form.component';
import { CargoLoadingState } from '../../store/cargo-loading.state';
import {
  Cargo,
  CarrierAdditionalPaymentForm,
  CarrierJson,
  CarrierJsonForm,
  CarrierJsonTransportFormWithGuid,
} from '../../../../types/cargo';
import { CargoLoadingService } from '../../services/cargo-loading.service';
import { PAYMENT_TYPES } from '../../../../types/payment-type';

@Component({
  selector: 'app-carrier',
  standalone: true,
  imports: [
    TuiIslandModule,
    ReactiveFormsModule,
    TuiInputModule,
    TuiButtonModule,
    TuiTextfieldControllerModule,
    TuiActiveZoneModule,
    TuiInputNumberModule,
    AutocompleteComponent,
    AsyncPipe,
    TransportItemFormComponent,
    TuiDataListDropdownManagerModule,
    TuiDataListModule,
    TuiDropdownModule,
  ],
  templateUrl: './carrier.component.html',
  styleUrl: './carrier.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarrierComponent implements OnInit {
  openCarrierDialog$ = new Subject<'create' | 'edit'>();

  carrierForm = this.fb.group<CarrierJsonForm>({
    id: this.fb.control<Nullable<number>>(null, { updateOn: 'blur' }),
    uniqueKey: this.fb.control(null),
    name: this.fb.control(null),
    cost: this.fb.control<Nullable<number>>(null),
    currencyLetter: this.fb.control(null),
    paymentType: this.fb.control(null),
    transports: this.fb.array<FormGroup<CarrierJsonTransportFormWithGuid>>([]),
    additionalPayments: this.fb.array<FormGroup<CarrierAdditionalPaymentForm>>(
      [],
    ),
  });

  openAttachmentsDialog$ = new Subject<{
    attachmentId: Nullable<number>;
    index: number;
  }>();

  zoneChanged$ = new Subject<boolean>();
  cargo$ = this.store.select(CargoLoadingState.getSelectedCargo);

  protected readonly CURRENCIES = CURRENCIES;
  protected readonly PAYMENT_TYPES = PAYMENT_TYPES;
  constructor(
    private readonly store: Store,
    private readonly fb: FormBuilder,
    private readonly destroyRef: DestroyRef,
    private readonly alert: AlertService,
    protected readonly cargoLoadingService: CargoLoadingService,
    private readonly dialogs: TuiDialogService,
    protected readonly catalogs: CargoCatalogsService,
  ) {}

  onUpdate() {
    this.zoneChanged$
      .pipe(
        delay(0),
        filter(focused => !focused && !this.carrierForm.pristine),
        withLatestFrom(this.cargo$),
        switchMap(([, cargo]) => this.saveChanges(cargo)),
        tap(() => {
          this.alert.showSuccess();
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onAttachments() {
    this.openAttachmentsDialog$
      .pipe(
        withLatestFrom(this.cargo$, this.cargoLoadingService.readonly$),
        switchMap(([{ attachmentId, index }, cargo, readonly]) => {
          const data: AttachmentsManagerInputData = {
            cargoId: cargo?.id ?? 0,
            attachmentId,
            canUpload: !readonly,
            canDelete: !readonly,
          };
          return this.dialogs
            .open<Nullable<number>>(
              new PolymorpheusComponent(AttachmentsManagerComponent),
              {
                size: 'auto',
                data,
              },
            )
            .pipe(
              map(updatedAttachmentId => {
                return {
                  updatedAttachmentId,
                  attachmentId,
                  index,
                };
              }),
            );
        }),
        withLatestFrom(this.cargo$),
        switchMap(([{ updatedAttachmentId, index }, cargo]) => {
          const attachmentIdControl =
            this.carrierForm.controls.transports.at(index).controls
              .attachmentId;
          attachmentIdControl.setValue(updatedAttachmentId);
          attachmentIdControl.markAsTouched();
          attachmentIdControl.markAsDirty();
          return this.saveChanges(cargo);
        }),
        catchError(() => {
          this.alert.showError();
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onOpenCarrierDialogClick() {
    this.openCarrierDialog$
      .pipe(
        switchMap(type => {
          return this.dialogs.open(
            new PolymorpheusComponent(CarrierFormComponent),
            {
              size: 'auto',
              dismissible: false,
              closeable: false,
              data:
                type === 'edit' ? this.getCarrierFormInputData() : undefined,
            },
          );
        }),
        tap(() => this.catalogs.fetchCarriers$.next()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  getCarrierFormInputData(): CarrierFormInputData | undefined {
    const { id } = this.carrierForm.value;
    const carrierFormCatalog = id
      ? this.catalogs.carrierMap.get(id.toString())
      : undefined;
    return {
      carrier: carrierFormCatalog,
    };
  }

  setCarrierDetails(id: Nullable<number>) {
    const carrier = this.catalogs.carrierMap.get(id?.toString() ?? '');
    const nameControl = this.carrierForm.controls.name;
    const uniqueKeyControl = this.carrierForm.controls.uniqueKey;
    nameControl.setValue(carrier?.name ?? null);
    uniqueKeyControl.setValue(carrier?.uniqueKey ?? null);
    nameControl.markAsTouched();
    nameControl.markAsDirty();
    uniqueKeyControl.markAsTouched();
    uniqueKeyControl.markAsDirty();
  }

  saveChanges(cargo: Nullable<Cargo>) {
    const value = this.carrierForm.getRawValue();
    const carrierJson: CarrierJson = {
      ...value,
      transports: value.transports.map(({ guid, ...transport }) => transport),
    };
    const payload: Cargo = {
      ...cargo,
      carrierJson,
    } as Cargo;
    return this.cargoLoadingService.updateCargo(payload).pipe(
      tap(() => {
        this.carrierForm.markAsPristine();
        this.carrierForm.markAsUntouched();
      }),
    );
  }

  addAdditionalPayment() {
    const additionalPaymentFormFormGroup =
      this.fb.group<CarrierAdditionalPaymentForm>({
        comment: this.fb.control(null),
        sum: this.fb.control(null),
        currencyLetter: this.fb.control(null),
      });
    this.carrierForm.controls.additionalPayments.push(
      additionalPaymentFormFormGroup,
    );
    this.markAsTouchedAndDirty();
  }

  removeAdditionalPayment(index: number) {
    this.carrierForm.controls.additionalPayments.removeAt(index);
    this.markAsTouchedAndDirty();
  }

  markAsTouchedAndDirty() {
    this.carrierForm.markAsTouched();
    this.carrierForm.markAsDirty();
  }

  addTransport() {
    const transport: FormGroup<CarrierJsonTransportFormWithGuid> =
      this.fb.group({
        guid: generateUUID(),
        transportNumber: this.fb.control(''),
        trailerNumber: this.fb.control(''),
        driverSurname: this.fb.control(''),
        driverName: this.fb.control(''),
        driverPhone: this.fb.control(''),
        comment: this.fb.control(''),
        loadingDate: this.fb.control(''),
        transportWeight: this.fb.control<Nullable<number>>(null),
        trailerWeight: this.fb.control<Nullable<number>>(null),
        attachmentId: this.fb.control<Nullable<number>>(null),
      });
    this.carrierForm.controls.transports.push(transport);
    this.markAsTouchedAndDirty();
  }

  removeTransport(index: number) {
    this.carrierForm.controls.transports.removeAt(index);
    this.markAsTouchedAndDirty();
  }

  fillFormOnStateChange() {
    this.cargo$
      .pipe(
        withLatestFrom(this.cargoLoadingService.readonly$),
        tap(([cargo, readonly]) => {
          const carrier = cargo?.carrierJson;
          if (carrier) {
            const { transports, additionalPayments, ...carrierDetails } =
              carrier;
            this.carrierForm.controls.transports.clear();
            this.carrierForm.controls.additionalPayments.clear();
            this.carrierForm.patchValue(
              { ...carrierDetails },
              { emitEvent: false },
            );
            transports.forEach(transport => {
              const group = this.fb.group({
                ...transport,
                guid: generateUUID(),
              });
              if (readonly) {
                group.disable();
              }
              this.carrierForm.controls.transports.push(group, {
                emitEvent: false,
              });
            });
            additionalPayments.forEach(payment => {
              const group = this.fb.group(payment);
              if (readonly) {
                group.disable();
              }
              this.carrierForm.controls.additionalPayments.push(group, {
                emitEvent: false,
              });
            });
          } else {
            this.carrierForm.reset();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  disableWhenReadonly() {
    this.cargoLoadingService.readonly$
      .pipe(
        tap(readonly => {
          if (readonly) {
            this.carrierForm.disable();
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  fillCarrierDetailsOnIdChange() {
    this.carrierForm.controls.id.valueChanges
      .pipe(
        tap(id => {
          this.setCarrierDetails(id);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit() {
    this.fillCarrierDetailsOnIdChange();
    this.onAttachments();
    this.fillFormOnStateChange();
    this.onUpdate();
    this.onOpenCarrierDialogClick();
    this.disableWhenReadonly();
  }
}
